import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { hashKey, useQuery, useQueryClient, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import { DateTime } from 'luxon';

import { POLL_INTERVAL_MS } from 'components/Graph/components/ExternalKnockoutChart/knockoutChartConstants';
import type { CandlestickGraphPoint } from 'components/Graph/components/types';
import { fetchWithAwsWaf } from 'lib/security/awsWaf';
import * as datadogRum from 'lib/telemetry/datadogRum';
import type { GetCryptoCurrentResponse, GetCryptoHistoricalResponse } from 'models/reqres';
import type { ExternaSourceVo, ExternalSourceDirectory, ExternalSourceItem } from 'models/underlying';
import type { PollingFrequency } from 'transport';
import config from 'utils/config';

import {
    consumeDocumentHistoryPreload,
    getExternalSourceJsonUrl,
    type ExternalSourceJsonSuffix,
} from './externalSourceUrl';
import { mergeSeriesSnapshot, samplesFromAnchoredValues, type KnockoutSeriesSample } from './knockoutSeries';

export const externalSourceKeys = {
    all: ['externalSource'] as const,
    historical: (item: ExternalSourceItem) => [...externalSourceKeys.all, 'historical', item] as const,
    lastValue: (item: ExternalSourceItem) => [...externalSourceKeys.all, 'lastValue', item] as const,
};

export interface ExternalSourceHistoricalData {
    candlesticksBySize: Record<string, CandlestickGraphPoint[]>;
    /** The live series, oldest first. Every producer writes it through `queries/knockoutSeries`. */
    samples: KnockoutSeriesSample[];
    /** Newest sample's time: the anchor the live engine plays behind and dates its frame by. */
    maturityMs: number;
    /**
     * Set to `maturityMs` when the chart's own event-tail poll (`useKnockoutEventTail`) advanced the
     * series to it. Named for the WebSocket lane that set it before the tail replaced it.
     *
     * Three writers share this entry: the historical fetch, the tail, and the current-value request's
     * candlestick merge, which does not move `maturityMs`. Without this, a series the chart's own tail
     * extended is indistinguishable from one the history request delivered, which is the difference
     * between crediting a data preload and not. A history result never carries it, even when it keeps
     * an extended tail (that series *was* delivered by the fetch).
     */
    wsAdvancedMaturityMs?: number;
}

/** Browser-side phases of one historical request. */
export interface ExternalSourceHistoricalRequestObserver {
    onQueryResolved: () => void;
    onRequestFailed: () => void;
    onRequestStarted: () => void;
    onResponseParsed: () => void;
    onResponseReceived: () => void;
}

type ExternalSourceHistoricalRequestPhase = keyof ExternalSourceHistoricalRequestObserver;

/** Telemetry must never change whether a price-data request succeeds. */
function notifyHistoricalRequestObserver(
    observer: Partial<ExternalSourceHistoricalRequestObserver> | undefined,
    phase: ExternalSourceHistoricalRequestPhase,
): void {
    try {
        observer?.[phase]?.();
    } catch {
        // Best-effort diagnostics only.
    }
}

// One-shot: fire at most one knockout 503 alert per page load to avoid flooding Datadog during outages.
let knockout503AlertFired = false;

async function fetchExternalSourceJson<T>(
    directory: ExternalSourceDirectory,
    item: ExternalSourceItem,
    suffix: ExternalSourceJsonSuffix,
    signal?: AbortSignal,
    requestObserver?: Pick<ExternalSourceHistoricalRequestObserver, 'onResponseReceived' | 'onResponseParsed'>,
    isFirstLoad = false,
): Promise<T> {
    const url = getExternalSourceJsonUrl(directory, item, suffix);
    // `cache: 'no-cache'` forces a revalidation on every poll. Without it the browser's default cache mode
    // serves the `max-age=1` response back, so polling faster than one second returns the same bytes and
    // cannot see a newly published value: the timer speeds up while the data does not. Revalidating rather
    // than bypassing the cache outright keeps it cheap, since the origin sends `etag`/`last-modified` and
    // answers an unchanged file with a 304 and no body.
    //
    // A first load is the exception. Nothing is on screen yet and the next poll revalidates, so it adopts
    // the parser-started response when there is one and otherwise accepts a copy under a second old. It is
    // decided from the cache rather than by whichever observer happens to run the request, because every
    // observer of a key shares one Query and any of their options can be the ones a refetch runs with.
    const documentPreload = isFirstLoad && suffix === 'history' ? consumeDocumentHistoryPreload(url) : undefined;
    const res = await (documentPreload ??
        fetchWithAwsWaf(url, { signal, cache: isFirstLoad ? 'default' : 'no-cache' }));
    notifyHistoricalRequestObserver(requestObserver, 'onResponseReceived');
    if (!res.ok) {
        if (res.status === 503 && !knockout503AlertFired) {
            knockout503AlertFired = true;
            try {
                datadogRum.addAction('knockout_external_source_503', { endpoint: suffix, directory, item });
            } catch {
                // Datadog may not be initialized
            }
        }
        throw new Error(`External source ${suffix} fetch failed: HTTP ${res.status} for ${directory}/${item}`);
    }
    const parsed = await res.json();
    notifyHistoricalRequestObserver(requestObserver, 'onResponseParsed');
    return parsed;
}

interface RawExternalSourcePoint {
    close: string;
    open: string;
    high: string;
    low: string;
    datetime: string;
}

function mapExternalSourceVo(vo: ExternaSourceVo): RawExternalSourcePoint {
    return {
        close: vo.close,
        open: vo.open,
        high: vo.high,
        low: vo.low,
        datetime: DateTime.fromMillis(vo.open_ts_ms).toISO()!,
    };
}

// Unconditionally shifts every point's time to the next point's time (unlike the
// 6-index families' same-day-gated smoothing) — preserved as-is from the original.
function shiftToNextTimestamp(points: RawExternalSourcePoint[]): RawExternalSourcePoint[] {
    const result = points.map((p) => ({ ...p }));
    for (let i = 0; i < result.length - 1; i++) {
        result[i].datetime = result[i + 1].datetime;
    }
    return result;
}

function parseFloatOr0(value: string): number {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
}

function toChartPoint(p: RawExternalSourcePoint): CandlestickGraphPoint {
    return {
        closeValue: parseFloatOr0(p.close),
        openValue: parseFloatOr0(p.open),
        highValue: parseFloatOr0(p.high),
        lowValue: parseFloatOr0(p.low),
        datetime: new Date(p.datetime),
    };
}

async function fetchExternalSourceHistorical(
    directory: ExternalSourceDirectory,
    item: ExternalSourceItem,
    signal?: AbortSignal,
    requestObserver?: Pick<ExternalSourceHistoricalRequestObserver, 'onResponseReceived' | 'onResponseParsed'>,
    isFirstLoad?: boolean,
): Promise<ExternalSourceHistoricalData> {
    const resp = await fetchExternalSourceJson<GetCryptoHistoricalResponse>(
        directory,
        item,
        'history',
        signal,
        requestObserver,
        isFirstLoad,
    );
    const candlesticksBySize: Record<string, CandlestickGraphPoint[]> = {};
    for (const [size, candles] of Object.entries(resp.candlesticks)) {
        const raw = candles.map(mapExternalSourceVo);
        candlesticksBySize[size] = shiftToNextTimestamp(raw).map(toChartPoint);
    }
    return {
        candlesticksBySize,
        samples: samplesFromAnchoredValues(resp.timeseries ?? [], resp.maturity_ts_ms),
        maturityMs: resp.maturity_ts_ms,
    };
}

// Merges a "current" tick into the historical cache — mirrors the original
// `loadExternalSourceLastValue`, which wrote into `externalSourceHistoricalData`
// in addition to the last-value fields. Only `candlesticksBySize` is touched;
// `maturityMs`/`samples` stay as the series producers left them.
/** Exported for tests: the marker-preservation invariant below is easy to drop and costly to lose. */
export function mergeCandlestickIntoHistorical(
    current: ExternalSourceHistoricalData | undefined,
    resp: GetCryptoCurrentResponse,
): ExternalSourceHistoricalData {
    const candlesticksBySize = { ...(current?.candlesticksBySize ?? {}) };
    for (const [size, vo] of Object.entries(resp.candlesticks)) {
        const point = toChartPoint(mapExternalSourceVo(vo));
        const existing = candlesticksBySize[size] ?? [];
        const last = existing[existing.length - 1];
        candlesticksBySize[size] =
            !last || last.datetime.getTime() !== point.datetime.getTime()
                ? [...existing, point]
                : [...existing.slice(0, -1), point];
    }
    // Carries the series through untouched — including who last advanced it. This merge changes only
    // the candlesticks, so dropping the marker here would let the next current-value poll disguise a
    // tail-extended series as a fetched one.
    return {
        candlesticksBySize,
        samples: current?.samples ?? [],
        maturityMs: current?.maturityMs ?? 0,
        wsAdvancedMaturityMs: current?.wsAdvancedMaturityMs,
    };
}

type ExternalSourcePollingFrequency = PollingFrequency | number;

function resolveExternalSourceHistoricalPollMs(polling: ExternalSourcePollingFrequency): number | undefined {
    if (!polling) return undefined;
    if (typeof polling === 'number') return polling;
    if (polling === 'slow') return config.slowPollingInterval;
    if (polling === 'crypto15m') return POLL_INTERVAL_MS;
    return config.pollingInterval;
}

function resolveExternalSourceLastValuePollMs(polling: ExternalSourcePollingFrequency): number | undefined {
    if (!polling) return undefined;
    if (typeof polling === 'number') return polling;
    if (polling === 'slow') return config.slowPollingInterval;
    if (polling === 'crypto15m') return POLL_INTERVAL_MS;
    return 2000;
}

/**
 * The fetching observer for an asset's `*_history.json`.
 *
 * A Query refetches with the options of whichever observer installed them last, disabled observers included, so
 * every observer of this key has to fetch the same way: `requestObserver` is the one per-caller input, and it only
 * reports timings. For the same reason this must not be mounted on a key another lane fetches, even disabled.
 * Readers that never fetch use `useExternalSourceHistoricalData`.
 */
export function useExternalSourceHistoricalQuery(
    directory: ExternalSourceDirectory,
    item: ExternalSourceItem,
    polling?: ExternalSourcePollingFrequency,
    enabled = true,
    requestObserver?: ExternalSourceHistoricalRequestObserver,
): UseQueryResult<ExternalSourceHistoricalData> {
    const pollMs = resolveExternalSourceHistoricalPollMs(polling);
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: externalSourceKeys.historical(item),
        queryFn: async ({ signal }) => {
            notifyHistoricalRequestObserver(requestObserver, 'onRequestStarted');
            try {
                const isFirstLoad =
                    queryClient.getQueryData<ExternalSourceHistoricalData>(externalSourceKeys.historical(item)) ===
                    undefined;
                const fetched = await fetchExternalSourceHistorical(
                    directory,
                    item,
                    signal,
                    requestObserver,
                    isFirstLoad,
                );

                // Read again after the request: `useKnockoutEventTail` appends newer seconds to this entry
                // while it is in flight, and those run ahead of the snapshot.
                const cached = queryClient.getQueryData<ExternalSourceHistoricalData>(
                    externalSourceKeys.historical(item),
                );
                const merged = mergeSeriesSnapshot(cached, fetched);
                notifyHistoricalRequestObserver(requestObserver, 'onQueryResolved');
                return merged;
            } catch (error) {
                notifyHistoricalRequestObserver(requestObserver, 'onRequestFailed');
                throw error;
            }
        },
        enabled,
        staleTime: pollMs ?? 5 * 60_000,
        refetchInterval: pollMs ?? false,
        // Hidden tabs do not render the live chart. Stop the timer and refresh once on return
        // instead of accumulating duplicate S3 reads while the browser is throttled.
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: !!pollMs,
    });
}

/**
 * Reads one cache entry's data and error state without becoming an observer of it.
 *
 * An observer installs its options on the Query, fetcher included, and a refetch runs with whichever were installed
 * last, even a disabled observer's. A reader mounted on a key another lane fetches (commodities, from the asset feed)
 * could therefore make that key refetch from `*_history.json`. Passing no fetcher is not an option either: React
 * Query logs an error on every render of such an observer in development, which for the knockout engine is 30 a
 * second. Re-renders only when the data or the error state changes, as a tracked observer would.
 */
function useCachedQueryEntry<TData>(queryKey: QueryKey): ExternalSourceCachedData<TData> {
    const queryCache = useQueryClient().getQueryCache();
    const queryHash = hashKey(queryKey);
    const subscribe = useCallback(
        (onStoreChange: () => void) =>
            queryCache.subscribe((event) => {
                if (event.query.queryHash === queryHash) onStoreChange();
            }),
        [queryCache, queryHash],
    );
    const readData = (): TData | undefined => queryCache.get<TData, Error, TData>(queryHash)?.state.data;
    const readIsError = (): boolean => queryCache.get(queryHash)?.state.status === 'error';
    return {
        data: useSyncExternalStore(subscribe, readData, readData),
        isError: useSyncExternalStore(subscribe, readIsError, readIsError),
    };
}

export interface ExternalSourceCachedData<TData> {
    data: TData | undefined;
    /** The last request for this entry failed, whether or not earlier data is still cached. */
    isError: boolean;
}

/** Reads an asset's historical entry without fetching it or registering as an observer of it. */
export function useExternalSourceHistoricalData(
    item: ExternalSourceItem,
): ExternalSourceCachedData<ExternalSourceHistoricalData> {
    return useCachedQueryEntry<ExternalSourceHistoricalData>(externalSourceKeys.historical(item));
}

/** Reads an asset's last-value entry without fetching it or registering as an observer of it. */
export function useExternalSourceLastValueData(
    item: ExternalSourceItem,
): ExternalSourceCachedData<{ lastValue: number; maturityMs: number }> {
    return useCachedQueryEntry<{ lastValue: number; maturityMs: number }>(externalSourceKeys.lastValue(item));
}

export function useExternalSourceLastValueQuery(
    directory: ExternalSourceDirectory,
    item: ExternalSourceItem,
    polling?: ExternalSourcePollingFrequency,
    enabled = true,
): UseQueryResult<{ lastValue: number; maturityMs: number }> {
    const queryClient = useQueryClient();
    const pollMs = resolveExternalSourceLastValuePollMs(polling);
    return useQuery({
        queryKey: externalSourceKeys.lastValue(item),
        queryFn: async ({ signal }) => {
            const resp = await fetchExternalSourceJson<GetCryptoCurrentResponse>(directory, item, 'current', signal);
            queryClient.setQueryData<ExternalSourceHistoricalData>(externalSourceKeys.historical(item), (current) =>
                current ? mergeCandlestickIntoHistorical(current, resp) : current,
            );
            const entries = Object.values(resp.candlesticks);
            const last = entries[entries.length - 1];
            return { lastValue: last ? parseFloatOr0(last.close) : 0, maturityMs: resp.maturity_ts_ms };
        },
        enabled,
        staleTime: pollMs ?? 5 * 60_000,
        refetchInterval: pollMs ?? false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: !!pollMs,
    });
}

/**
 * Shared by ExternalIndicesWidget.tsx and ExternalIndicesGraphWrapper.tsx —
 * the two Harris/Trump election-index chart consumers. Two fixed hook calls,
 * not a loop over `indices`, since this pair is the only combination ever used.
 */
export function useExternalIndicesChartData(
    enabled: boolean,
    polling: PollingFrequency = 'slow',
): [Record<string, CandlestickGraphPoint[]>, Record<string, CandlestickGraphPoint>] {
    const trump = useExternalSourceHistoricalQuery('indices', 'Trump', polling, enabled);
    const harris = useExternalSourceHistoricalQuery('indices', 'Harris', polling, enabled);

    const trumpCandles = trump.data?.candlesticksBySize['15M'];
    const harrisCandles = harris.data?.candlesticksBySize['15M'];
    if (!enabled || !trumpCandles || !harrisCandles) {
        return [{}, {}];
    }

    const graphValues: Record<string, CandlestickGraphPoint[]> = { Trump: trumpCandles, Harris: harrisCandles };
    const positionValues: Record<string, CandlestickGraphPoint> = {
        Trump: trumpCandles[trumpCandles.length - 1],
        Harris: harrisCandles[harrisCandles.length - 1],
    };
    return [graphValues, positionValues];
}

export function isExternalSourceDelayed(maturityMs: number | undefined): boolean {
    if (!maturityMs) return false;
    const maturityAgeSeconds = Math.abs(DateTime.fromMillis(maturityMs).diffNow('seconds').seconds);
    // Delayed if the last value is older than 30 seconds
    return maturityAgeSeconds > 30;
}

/** A stale feed must stay stale this long before the UI is allowed to say so. */
const DELAYED_APPEARANCE_DEBOUNCE_MS = 5_000;
/** Once the warning is up, it stays up this long even if the feed recovers immediately. */
const DELAYED_MIN_VISIBLE_MS = 2_000;
/**
 * How often the delayed state is re-derived. Also the precision of the two
 * durations above, so it has to be well under `DELAYED_MIN_VISIBLE_MS`.
 */
const DELAYED_REEVALUATE_MS = 500;

interface ExternalSourceDelayedOptions {
    /** Skip the timer entirely — the chart is hidden, or has no projection to date. */
    enabled?: boolean;
    /**
     * A staleness the age check cannot see because the caller knows it first:
     * the knockout playhead holding its last frame on a stalled series feed. It
     * runs through the same debounce as the age check, so a hold that clears on
     * the next frame doesn't flash the warning either.
     */
    feedReportsDelayed?: boolean;
}

/**
 * Debounced form of `isExternalSourceDelayed`, for anything that *renders* a
 * delayed-data warning.
 *
 * `isExternalSourceDelayed` is a bare clock read: it answers for the instant it
 * is called and nothing re-renders when its answer changes. A caller that
 * inlines it therefore shows the warning on whatever cadence its own renders
 * happen to have — on the crypto charts that is every price tick, and a
 * `maturityMs` sitting near the 30s threshold makes the warning strobe.
 *
 * This hook fixes that three ways: it owns a timer, so the answer advances on
 * the clock instead of under render pressure; it requires the feed to stay
 * stale for `DELAYED_APPEARANCE_DEBOUNCE_MS` before showing the warning, so a
 * momentary gap between polls never surfaces at all; and it holds the warning
 * for `DELAYED_MIN_VISIBLE_MS` once shown, so a poll landing right after it
 * appears can't blink it away mid-read.
 *
 * `feedReportsDelayed` is a second way in for callers that detect a stall the
 * age check can't see. It is debounced identically — the two are ORed *before*
 * the debounce, so a value that is old by one measure and fresh by the other
 * still counts as one continuous stretch of staleness.
 *
 * The debounce runs from when this hook first *sees* the feed stale, not from
 * when it went stale. For a mount that outlives the transition that means a
 * single effective threshold — the warning appears once the value is ~35s old,
 * and a fresher `maturityMs` arriving before then resets the count rather than
 * deferring it. For a mount that lands on an already-stale value it means 5s
 * from mount, which is the point: a slide rotating in on a cached `maturityMs`
 * gets a window to refresh before it is allowed to accuse the feed.
 *
 * Returns `false` on the server and on the first client render — the clock is
 * read only from the effect. That keeps SSR and hydration in agreement and
 * keeps a render-time `Date.now()` out of the prerender path.
 *
 * `ExternalKnockoutHeaderRedesign` deliberately still calls the predicate
 * directly: it flips a one-word column label rather than adding a line of
 * warning text, so it has no flicker worth a timer.
 */
export function useExternalSourceDelayed(
    maturityMs: number | undefined,
    { enabled = true, feedReportsDelayed = false }: ExternalSourceDelayedOptions = {},
): boolean {
    const [delayed, setDelayed] = useState(false);
    // Mirrors `delayed` so the interval can read the live value without being torn
    // down and rebuilt every time it flips.
    const delayedRef = useRef(false);
    // When the feed was first *seen* stale, and when the warning went up. Both
    // outlive the effect so a changing `maturityMs` doesn't restart the count.
    const staleSinceRef = useRef<number | undefined>(undefined);
    const shownAtRef = useRef<number | undefined>(undefined);

    useEffect(() => {
        if (!enabled) {
            staleSinceRef.current = undefined;
            shownAtRef.current = undefined;
            delayedRef.current = false;
            setDelayed(false);
            return undefined;
        }

        const reevaluate = (): void => {
            const now = Date.now();
            const stale = feedReportsDelayed || isExternalSourceDelayed(maturityMs);
            if (!stale) staleSinceRef.current = undefined;
            else if (staleSinceRef.current === undefined) staleSinceRef.current = now;

            const next = delayedRef.current
                ? stale || now - (shownAtRef.current ?? now) < DELAYED_MIN_VISIBLE_MS
                : staleSinceRef.current !== undefined && now - staleSinceRef.current >= DELAYED_APPEARANCE_DEBOUNCE_MS;

            if (next === delayedRef.current) return;
            if (next) shownAtRef.current = now;
            delayedRef.current = next;
            setDelayed(next);
        };

        reevaluate();
        const timer = setInterval(reevaluate, DELAYED_REEVALUATE_MS);
        return () => clearInterval(timer);
    }, [maturityMs, enabled, feedReportsDelayed]);

    return delayed;
}

export function externalLastValue(lastValue: number | undefined, maturityMs: number | undefined): number {
    return isExternalSourceDelayed(maturityMs) ? 0 : (lastValue ?? 0);
}
